import { EventBus } from './Engine.js';

/**
 * Pointer-lock + keyboard/mouse input. Frame-coherent: consumers read `state`
 * during fixed()/update(); edge flags are cleared at the end of each frame.
 */
export class Input {
  name = 'input';
  priority = 0;

  constructor(canvas) {
    this.canvas = canvas;
    this.locked = false;
    this.enabled = true;
    this.sensitivity = 0.0022;
    this.adsSensitivityScale = 0.72;
    this.invertY = false;

    this.state = {
      forward: 0, right: 0,      // -1..1
      jump: false, jumpEdge: false,
      crouch: false, crouchEdge: false,
      sprint: false,
      fire: false, fireEdge: false,
      ads: false, adsEdge: false,
      reload: false,
      melee: false, meleeEdge: false,
      lean: 0,
      slot: 0,                    // 1..4 weapon slot request, 0 = none
      nextWeapon: false,
      dx: 0, dy: 0,               // accumulated mouse delta this frame (radians applied by player)
      wheel: 0,
    };

    this.keys = new Set();
    this._bind();
  }

  _bind() {
    const c = this.canvas;
    this._onKeyDown = (e) => {
      if (!this.enabled) return;
      if (e.repeat) { return; }
      this.keys.add(e.code);
      this._applyKeyEdge(e.code, true);
      if (['Space', 'ArrowUp', 'ArrowDown', 'Tab'].includes(e.code)) e.preventDefault();
    };
    this._onKeyUp = (e) => {
      this.keys.delete(e.code);
      this._applyKeyEdge(e.code, false);
    };
    this._onMouseMove = (e) => {
      if (!this.locked || !this.enabled) return;
      this.state.dx += e.movementX || 0;
      this.state.dy += e.movementY || 0;
    };
    this._onMouseDown = (e) => {
      if (!this.enabled) return;
      if (!this.locked) { c.requestPointerLock?.(); return; }
      if (e.button === 0) { this.state.fire = true; this.state.fireEdge = true; }
      if (e.button === 2) { this.state.ads = true; this.state.adsEdge = true; }
      if (e.button === 1) { this.state.melee = true; this.state.meleeEdge = true; }
    };
    this._onMouseUp = (e) => {
      if (e.button === 0) this.state.fire = false;
      if (e.button === 2) this.state.ads = false;
      if (e.button === 1) this.state.melee = false;
    };
    this._onWheel = (e) => { this.state.wheel += Math.sign(e.deltaY); };
    this._onLockChange = () => {
      this.locked = document.pointerLockElement === c;
      if (!this.locked) this._releaseAll();
    };
    this._onContext = (e) => e.preventDefault();
    this._onBlur = () => this._releaseAll();

    window.addEventListener('keydown', this._onKeyDown);
    window.addEventListener('keyup', this._onKeyUp);
    window.addEventListener('mousemove', this._onMouseMove);
    c.addEventListener('mousedown', this._onMouseDown);
    window.addEventListener('mouseup', this._onMouseUp);
    window.addEventListener('wheel', this._onWheel, { passive: true });
    document.addEventListener('pointerlockchange', this._onLockChange);
    c.addEventListener('contextmenu', this._onContext);
    window.addEventListener('blur', this._onBlur);
  }

  _releaseAll() {
    this.keys.clear();
    const s = this.state;
    s.forward = s.right = 0;
    s.jump = s.crouch = s.sprint = s.fire = s.ads = s.reload = s.melee = false;
    s.lean = 0;
  }

  _applyKeyEdge(code, down) {
    const s = this.state;
    switch (code) {
      case 'Space': s.jump = down; if (down) s.jumpEdge = true; break;
      case 'ControlLeft': case 'KeyC': s.crouch = down; if (down) s.crouchEdge = true; break;
      case 'ShiftLeft': s.sprint = down; break;
      case 'KeyR': s.reload = down; break;
      case 'KeyV': s.melee = down; if (down) s.meleeEdge = true; break;
      case 'KeyQ': s.lean = down ? -1 : 0; break;
      case 'KeyE': s.lean = down ? 1 : 0; break;
      case 'Digit1': if (down) s.slot = 1; break;
      case 'Digit2': if (down) s.slot = 2; break;
      case 'Digit3': if (down) s.slot = 3; break;
      case 'Digit4': if (down) s.slot = 4; break;
      case 'KeyF': if (down) s.nextWeapon = true; break;
    }
  }

  /** Called by the engine before systems run. */
  update() {
    const k = this.keys;
    const s = this.state;
    s.forward = (k.has('KeyW') || k.has('ArrowUp') ? 1 : 0) - (k.has('KeyS') || k.has('ArrowDown') ? 1 : 0);
    s.right = (k.has('KeyD') || k.has('ArrowRight') ? 1 : 0) - (k.has('KeyA') || k.has('ArrowLeft') ? 1 : 0);
  }

  /** Called by the engine after all systems have run. */
  endFrame() {
    const s = this.state;
    s.dx = 0; s.dy = 0; s.wheel = 0;
    s.jumpEdge = s.crouchEdge = s.fireEdge = s.adsEdge = s.meleeEdge = false;
    s.slot = 0;
    s.nextWeapon = false;
  }

  dispose() {
    window.removeEventListener('keydown', this._onKeyDown);
    window.removeEventListener('keyup', this._onKeyUp);
    window.removeEventListener('mousemove', this._onMouseMove);
    this.canvas.removeEventListener('mousedown', this._onMouseDown);
    window.removeEventListener('mouseup', this._onMouseUp);
    window.removeEventListener('wheel', this._onWheel);
    document.removeEventListener('pointerlockchange', this._onLockChange);
    this.canvas.removeEventListener('contextmenu', this._onContext);
    window.removeEventListener('blur', this._onBlur);
  }
}

/** Engine system wrapper so input is refreshed at the right points in the frame. */
export function createInputSystem(canvas) {
  const input = new Input(canvas);
  return {
    name: 'input',
    priority: 0,
    input,
    init(ctx) { ctx.input = input; },
    update() { input.update(); },
    dispose() { input.dispose(); },
  };
}

/** Runs last, clearing per-frame edge state. */
export function createInputFlushSystem(inputSystem) {
  return {
    name: 'inputFlush',
    priority: 10000,
    update() { inputSystem.input.endFrame(); },
  };
}
